import React from "react";
import agenticAiImage from "../../assets/proposal-channels/agentic-ai.jpg";
import ctvImage from "../../assets/proposal-channels/ctv.jpg";
import inStoreImage from "../../assets/proposal-channels/in-store.jpg";
import offsiteImage from "../../assets/proposal-channels/offsite.jpg";
import pdpAmazonImage from "../../assets/proposal-channels/pdp-amazon.jpg";
import pdpD2cImage from "../../assets/proposal-channels/pdp-d2c.jpg";
import pdpImage from "../../assets/proposal-channels/pdp.jpg";
import socialImage from "../../assets/proposal-channels/social.jpg";
import sponsoredImage from "../../assets/proposal-channels/sponsored.jpg";
import { AGENTIC_AI_COPY } from "./strategyCopy";
import { BLACK, BORDER, CARD_BG, FONT, GOLD, GOLD_DARK, GOLD_TINT, TEXT_MUTED } from "./theme";
import {
    PDP_SUBTYPE_LABELS,
    PDP_SUBTYPES,
    PdpSubtype,
    SelectedChannel,
    WizardChannel,
    WizardObjective,
} from "./types";

// Copy for the 6 generic channels, carrying the concrete placement examples
// from the design doc (Email / retailer sites / AI shopping all live under
// Offsite in the channel taxonomy). Images are static assets keyed by channel
// name — a channel the API adds later falls back to the emoji icon.
const CHANNEL_COPY: Record<string, { icon: string; description: string; image?: string }> = {
    PDP: {
        icon: "🖥️",
        image: pdpImage,
        description: "Product detail pages — yours, Amazon's, and retailer sites — to educate and convert shoppers.",
    },
    Sponsored: {
        icon: "📣",
        image: sponsoredImage,
        description: "Retail media and paid placements to drive ROAS and capture high-intent shoppers.",
    },
    CTV: {
        icon: "📺",
        image: ctvImage,
        description: "Connected TV — reach viewers on the big screen with content that builds awareness and trust.",
    },
    // The channel is named "Offsite OLV" in the DB; the bare "Offsite" key is
    // kept for environments still on the pre-rename name.
    "Offsite OLV": {
        icon: "✉️",
        image: offsiteImage,
        description: "Email, retailer homepages, AI shopping experiences, and other offsite placements.",
    },
    Offsite: {
        icon: "✉️",
        image: offsiteImage,
        description: "Email, retailer homepages, AI shopping experiences, and other offsite placements.",
    },
    Social: {
        icon: "👍",
        image: socialImage,
        description: "Meta, TikTok, YouTube, and more — stop the scroll and drive action.",
    },
    "In-Store": {
        icon: "🏬",
        image: inStoreImage,
        description: "In-store screens and displays that influence purchase decisions at the shelf.",
    },
};

// RMN white-label portals: the PDP channel is the retailer's own PDP, so the
// generic "yours, Amazon's, and retailer sites" framing (and the sub-type
// pills) don't apply there.
const RMN_PDP_DESCRIPTION =
    "Retailer product detail pages — to educate and convert shoppers at the point of decision.";

// The PDP card's image follows the selected sub-type (retail is the default
// look); the sub-type is a refinement of the PDP channel, not a channel.
const PDP_SUBTYPE_IMAGES: Record<PdpSubtype, string> = {
    retail: pdpImage,
    d2c: pdpD2cImage,
    amazon: pdpAmazonImage,
};

const cardImageStyle: React.CSSProperties = {
    display: "block",
    width: "100%",
    aspectRatio: "16 / 10",
    objectFit: "cover",
    borderRadius: 10,
    border: `1px solid ${BORDER}`,
    background: CARD_BG,
    marginBottom: 10,
};

// Same expanded-array semantics as ChannelQuantityPicker
// (src/components/Orders/ChannelQuantityPicker.tsx): one array element per
// channel INSTANCE; duplicates = quantity; the ids array (with duplicates)
// is what the API receives as channel_ids.
interface Step2Props {
    channels: WizardChannel[];
    objective: WizardObjective | undefined;
    selectedChannels: SelectedChannel[];
    onChange: (channels: SelectedChannel[]) => void;
    pdpSubtype: PdpSubtype;
    onPdpSubtypeChange: (sub: PdpSubtype) => void;
    // RMN white-label portal lens: hides the Retail | D2C | Amazon pills
    // and swaps the PDP blurb. A retailer's proposal is always for its own
    // (retail) PDP, so the sub-type is not a choice there and the wizard
    // keeps the "retail" default.
    rmn?: boolean;
}

export function Step2Channels({
    channels,
    objective,
    selectedChannels,
    onChange,
    pdpSubtype,
    onPdpSubtypeChange,
    rmn = false,
}: Step2Props) {
    const suggestedIds = new Set((objective ? objective.channels : []).map((c) => c.id));

    const countFor = (id: number) => selectedChannels.filter((s) => s.id === id).length;
    const addOne = (ch: WizardChannel) => onChange([...selectedChannels, { id: ch.id, name: ch.name }]);
    const removeOne = (ch: WizardChannel) => {
        const idx = selectedChannels.map((s) => s.id).lastIndexOf(ch.id);
        if (idx === -1) return;
        onChange(selectedChannels.filter((_, i) => i !== idx));
    };

    const qtyButtonStyle: React.CSSProperties = {
        fontFamily: FONT,
        width: 26,
        height: 26,
        borderRadius: "50%",
        border: `1px solid ${GOLD_DARK}`,
        background: CARD_BG,
        color: GOLD_DARK,
        fontSize: 15,
        fontWeight: 700,
        cursor: "pointer",
        lineHeight: "22px",
        padding: 0,
    };

    // One instance of every channel not yet selected — existing quantities
    // are preserved.
    const selectAll = () => {
        const missing = channels
            .filter((ch) => countFor(ch.id) === 0)
            .map((ch) => ({ id: ch.id, name: ch.name }));
        if (missing.length > 0) onChange([...selectedChannels, ...missing]);
    };
    const allSelected = channels.every((ch) => countFor(ch.id) > 0);

    return (
        <div>
            <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 700, color: BLACK, margin: "0 0 6px" }}>
                Where will your videos be used?
            </h2>
            <div
                style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 12,
                    marginBottom: 20,
                }}
            >
                <p style={{ fontFamily: FONT, fontSize: 14, color: TEXT_MUTED, margin: 0, lineHeight: 1.6 }}>
                    We've preselected the channels that work best for your goal. Adjust the mix — and how
                    many placements per channel — to fit your plan.
                </p>
                <button
                    type="button"
                    onClick={selectAll}
                    disabled={allSelected}
                    style={{
                        fontFamily: FONT,
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: allSelected ? TEXT_MUTED : GOLD_DARK,
                        background: "transparent",
                        border: `1px solid ${allSelected ? BORDER : GOLD_DARK}`,
                        borderRadius: 99,
                        padding: "7px 14px",
                        cursor: allSelected ? "default" : "pointer",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                    }}
                >
                    Select all channels
                </button>
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                    gap: 12,
                }}
            >
                {channels.map((ch) => {
                    const count = countFor(ch.id);
                    const selected = count > 0;
                    const suggested = suggestedIds.has(ch.id);
                    const copy = CHANNEL_COPY[ch.name] || { icon: "🎬", description: "" };
                    const isPdp = ch.name === "PDP";
                    const image = isPdp ? PDP_SUBTYPE_IMAGES[pdpSubtype] : copy.image;
                    const description = isPdp && rmn ? RMN_PDP_DESCRIPTION : copy.description;
                    return (
                        <div
                            key={ch.id}
                            onClick={() => {
                                if (!selected) addOne(ch);
                            }}
                            style={{
                                fontFamily: FONT,
                                position: "relative",
                                background: selected ? GOLD_TINT : CARD_BG,
                                border: `1.5px solid ${selected ? GOLD_DARK : BORDER}`,
                                borderRadius: 14,
                                padding: "16px 14px",
                                cursor: selected ? "default" : "pointer",
                                transition: "border-color .15s, background .15s",
                            }}
                        >
                            {suggested && (
                                <span
                                    style={{
                                        position: "absolute",
                                        top: 10,
                                        right: 10,
                                        fontSize: 10,
                                        fontWeight: 700,
                                        letterSpacing: 0.4,
                                        color: GOLD_DARK,
                                        background: GOLD_TINT,
                                        borderRadius: 99,
                                        padding: "3px 8px",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    Recommended
                                </span>
                            )}
                            {image ? (
                                <img src={image} alt="" style={cardImageStyle} />
                            ) : (
                                <div style={{ fontSize: 22, marginBottom: 6 }}>{copy.icon}</div>
                            )}
                            <div style={{ fontSize: 14.5, fontWeight: 700, color: BLACK, marginBottom: 4 }}>
                                {ch.name}
                            </div>
                            <div
                                style={{
                                    fontSize: 12.5,
                                    color: TEXT_MUTED,
                                    lineHeight: 1.5,
                                    minHeight: 38,
                                    marginBottom: 10,
                                }}
                            >
                                {description}
                            </div>
                            {isPdp && !rmn && (
                                // Sub-type pills deliberately don't stop
                                // propagation: picking one on an unselected
                                // card also selects the PDP channel.
                                <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                                    {PDP_SUBTYPES.map((sub) => {
                                        const active = pdpSubtype === sub;
                                        return (
                                            <button
                                                key={sub}
                                                type="button"
                                                onClick={() => onPdpSubtypeChange(sub)}
                                                style={{
                                                    fontFamily: FONT,
                                                    fontSize: 11.5,
                                                    fontWeight: 600,
                                                    color: active ? "#fff" : GOLD_DARK,
                                                    background: active ? GOLD_DARK : "transparent",
                                                    border: `1px solid ${active ? GOLD_DARK : BORDER}`,
                                                    borderRadius: 99,
                                                    padding: "4px 10px",
                                                    cursor: "pointer",
                                                    transition: "background .15s, color .15s",
                                                }}
                                            >
                                                {PDP_SUBTYPE_LABELS[sub]}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                            {selected ? (
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <button
                                        type="button"
                                        aria-label={`Remove one ${ch.name}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeOne(ch);
                                        }}
                                        style={qtyButtonStyle}
                                    >
                                        −
                                    </button>
                                    <span style={{ fontSize: 13.5, fontWeight: 700, color: BLACK }}>
                                        {count} {count === 1 ? "placement" : "placements"}
                                    </span>
                                    <button
                                        type="button"
                                        aria-label={`Add another ${ch.name}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            addOne(ch);
                                        }}
                                        style={qtyButtonStyle}
                                    >
                                        +
                                    </button>
                                </div>
                            ) : (
                                <div style={{ fontSize: 12.5, fontWeight: 600, color: GOLD }}>+ Add channel</div>
                            )}
                        </div>
                    );
                })}

                {/* Always-on Agentic AI layer — presentational, never billed or
                    sent as a channel instance: every video ships AI-ready. */}
                <div
                    style={{
                        fontFamily: FONT,
                        position: "relative",
                        background: GOLD_TINT,
                        border: `1.5px solid ${GOLD_DARK}`,
                        borderRadius: 14,
                        padding: "16px 14px",
                    }}
                >
                    <span
                        style={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: 0.4,
                            color: "#fff",
                            background: GOLD_DARK,
                            borderRadius: 99,
                            padding: "3px 8px",
                            textTransform: "uppercase",
                        }}
                    >
                        Included
                    </span>
                    <img src={agenticAiImage} alt="" style={cardImageStyle} />
                    <div style={{ fontSize: 14.5, fontWeight: 700, color: BLACK, marginBottom: 4 }}>
                        {AGENTIC_AI_COPY.title}
                    </div>
                    <div
                        style={{
                            fontSize: 12.5,
                            color: TEXT_MUTED,
                            lineHeight: 1.5,
                            minHeight: 38,
                            marginBottom: 10,
                        }}
                    >
                        {AGENTIC_AI_COPY.description}
                    </div>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: GOLD_DARK }}>
                        ✓ {AGENTIC_AI_COPY.badge}
                    </div>
                </div>
            </div>
        </div>
    );
}
